import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Modal,
  Spinner,
  Alert,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import { AvForm } from "availity-reactstrap-validation";
import "./ExposedBlackBox.scss";

const ALL_SCAN_TYPES = [
  { id: "unauthenticated", label: "Unauthenticated (Safe Web/Port Scan)", intrusive: false, authMode: "unauthenticated" },
  { id: "auth_ssh", label: "Authenticated SSH (Intrusive)", intrusive: true, authMode: "authenticated" },
  { id: "auth_ssh_key", label: "Authenticated SSH Key (Intrusive)", intrusive: true, authMode: "authenticated" },
  { id: "auth_smb", label: "Authenticated SMB / Windows (Intrusive)", intrusive: true, authMode: "authenticated" },
];

const SEVERITY_CONFIGS = [
  { key: "critical", label: "Critical", match: ["critical"], color: "var(--sev-critical)", border: "var(--sev-critical)" },
  { key: "high", label: "High", match: ["high"], color: "var(--sev-high)", border: "var(--sev-high)" },
  { key: "medium", label: "Medium", match: ["medium", "med"], color: "var(--sev-medium)", border: "var(--sev-medium)" },
  { key: "low", label: "Low", match: ["low"], color: "var(--sev-low)", border: "var(--sev-low)" },
  { key: "info", label: "Informational", match: ["informational", "info"], color: "var(--sev-info)", border: "var(--slate-600)" },
];

const STATUS_OPTIONS = [
  "New",
  "Triaged",
  "Validated",
  "Remediated",
  "Resolved",
  "Accepted Risk",
  "False Positive",
  "Reopened",
];

const DEFAULT_COUNTS = {
  Critical: 0,
  High: 0,
  Medium: 0,
  Low: 0,
  Informational: 0,
};

const ExposedBlackBox = () => {
  const token = localStorage.getItem("token");
  const orgID = localStorage.getItem("orgID");
  const userID = localStorage.getItem("userID");
  const role = (localStorage.getItem("role") || "read_only").toLowerCase();
  const canManageScope = role === "super_admin" || role === "tenant_admin";
  const canRunScan = role === "super_admin" || role === "tenant_admin" || role === "analyst";

  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
  const theme = layoutMode === "light" ? "light" : "dark";

  // Findings & Task data states (initialized empty: real data loads only if a scan exists in DB)
  const [findings, setFindings] = useState([]);
  const [scanStatus, setScanStatus] = useState("idle"); // idle | running | completed | failed | killed
  const [latestScanInfo, setLatestScanInfo] = useState(null);
  const [latestTaskId, setLatestTaskId] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [isTriggering, setIsTriggering] = useState(false);
  const [isKilling, setIsKilling] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [isRescanning, setIsRescanning] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [copiedId, setCopiedId] = useState(false);

  // Scan History modal states
  const [modalScanHistory, setModalScanHistory] = useState(false);
  const [scanHistory, setScanHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [downloadingTaskId, setDownloadingTaskId] = useState(null);
  const [historySidebarOffset, setHistorySidebarOffset] = useState(0);

  // Scopes & Governance states
  const [scopes, setScopes] = useState([]);
  const [selectedScopeId, setSelectedScopeId] = useState("");
  const [verifiedAssets, setVerifiedAssets] = useState([]);
  const [modalCreateScope, setModalCreateScope] = useState(false);
  const [modalManageScopes, setModalManageScopes] = useState(false);
  const [isCreatingScope, setIsCreatingScope] = useState(false);

  // Create Scope Form state
  const [newScopeTarget, setNewScopeTarget] = useState("");
  const [newScopeTargetType, setNewScopeTargetType] = useState("domain");
  const [newScopeScanTypes, setNewScopeScanTypes] = useState(["unauthenticated"]);
  const [newScopeIntrusive, setNewScopeIntrusive] = useState(false);
  const [newScopeVapt, setNewScopeVapt] = useState(false);
  const [newScopeWindowStart, setNewScopeWindowStart] = useState("");
  const [newScopeWindowEnd, setNewScopeWindowEnd] = useState("");
  const [newScopeExpiry, setNewScopeExpiry] = useState("");
  const [newScopeRateLimit, setNewScopeRateLimit] = useState(1);

  // Scan Trigger Form state
  const [selectedScanType, setSelectedScanType] = useState("unauthenticated");
  const [selectedKind, setSelectedKind] = useState("va"); // "va" | "vapt"
  const [scanName, setScanName] = useState("");
  const companyName = "Glogix";

  // Authenticated Scan Credentials state
  const [credUsername, setCredUsername] = useState("");
  const [credPassword, setCredPassword] = useState("");
  const [credKey, setCredKey] = useState("");
  const [credPort, setCredPort] = useState(22);
  const [credDomain, setCredDomain] = useState("");

  // Severity counts
  const [counts, setCounts] = useState(DEFAULT_COUNTS);

  // Table filter & search & sort states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSeverityFilter, setSelectedSeverityFilter] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1); // 1 asc, -1 desc
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Slide-over Drawer state
  const [drawerFinding, setDrawerFinding] = useState(null);
  const [updatingFindingIndex, setUpdatingFindingIndex] = useState(null);

  // Polling ref
  const pollTimerRef = useRef(null);
  const copyTimeoutRef = useRef(null);

  // Initial load
  useEffect(() => {
    async function checkAuth() {
      if (!token) return;
      try {
        const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await response.json();
        if (!data.status) {
          window.location.href = "/login";
        }
      } catch (e) {
        console.error("Auth check error:", e);
      }
    }
    checkAuth();
    fetchUserProfile();
    fetchScopes();
    fetchVerifiedAssets();
    fetchTaskData();

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  async function fetchUserProfile() {
    if (!userID) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}user-profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userCheck: userID }),
      });
      const data = await response.json();
      if (data && data.user && data.user.Email) {
        setUserEmail(data.user.Email);
      }
    } catch (err) {
      console.warn("[fetchUserProfile] Error:", err);
    }
  }

  // Poll automatically every 6s while scan is running
  useEffect(() => {
    if (scanStatus === "running") {
      if (!pollTimerRef.current) {
        pollTimerRef.current = setInterval(() => {
          fetchTaskData();
        }, 6000);
      }
    } else {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  }, [scanStatus]);

  // Fetch authorized scopes
  async function fetchScopes() {
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}blackbox/scope/list`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgID, token }),
      });
      const data = await response.json();
      if (data && data.status && Array.isArray(data.scopes)) {
        setScopes(data.scopes);
        const activeList = data.scopes.filter((s) => s.status === "active");
        if (activeList.length > 0 && !selectedScopeId) {
          setSelectedScopeId(activeList[0]._id);
          const defaultType = activeList[0].scanTypesAllowed?.[0] || "unauthenticated";
          setSelectedScanType(defaultType);
          setScanName(`BlackBox-${activeList[0].target}`);
        }
      }
    } catch (err) {
      console.error("[fetchScopes] Error:", err);
    }
  }

  // Fetch verified assets
  async function fetchVerifiedAssets() {
    if (!token) return;
    const list = [];
    try {
      const dResp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}getassetlist-domain`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgID }),
      });
      const dData = await dResp.json();
      const rawDomains = dData.domainList || dData.assetList || [];
      rawDomains.forEach((item) => {
        const val = item.Domain || item.Value;
        const status = String(item.Status || "").toLowerCase();
        if (val && status === "verified") {
          list.push({
            id: item.ID || item._id,
            target: val,
            type: "domain",
            source: item.Source || "Manual",
            status: item.Status || "Verified",
          });
        }
      });

      const ipResp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}getassetlist-ip`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgID }),
      });
      const ipData = await ipResp.json();
      const rawIPs = ipData.ipList || ipData.assetList || [];
      rawIPs.forEach((item) => {
        const val = item.IP || item.Value;
        const status = String(item.Status || "").toLowerCase();
        if (val && status === "verified") {
          list.push({
            id: item.ID || item._id,
            target: val,
            type: "ip",
            source: item.Source || "Manual",
            status: item.Status || "Verified",
          });
        }
      });

      setVerifiedAssets(list);
      if (list.length > 0 && !newScopeTarget) {
        setNewScopeTarget(list[0].target);
        setNewScopeTargetType(list[0].type);
      }
    } catch (err) {
      console.error("[fetchVerifiedAssets] Error:", err);
    }
  }

  // Fetch latest TaskData record
  async function fetchTaskData() {
    if (!token) return;
    try {
      const featureIDForServer = 165112129704888;
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}taskData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userID,
          orgID,
          featureIDForServer,
        }),
      });

      const res = await response.json();
      if (res && res.data && res.data.Data) {
        const d = res.data.Data;
        setLatestScanInfo(d);
        setLatestTaskId(res.data._id || res.data.TaskID);
        setScanStatus(d.status || "completed");

        // Findings Table shows all completed VA + VAPT scans for this target, not just the latest scan
        if (d.target) {
          await fetchCombinedFindings(d.target);
        } else {
          setFindings([]);
          setCounts(DEFAULT_COUNTS);
        }
      } else {
        setLatestScanInfo(null);
        setLatestTaskId(null);
        setFindings([]);
        setScanStatus("idle");
        setCounts(DEFAULT_COUNTS);
      }
    } catch (err) {
      console.error("[ExposedBlackBox] Error fetching task data:", err);
      setLatestScanInfo(null);
      setLatestTaskId(null);
      setFindings([]);
      setScanStatus("idle");
      setCounts(DEFAULT_COUNTS);
    }
  }

  // Fetch findings from every completed VA/VAPT scan of the target, concatenated (no dedup)
  async function fetchCombinedFindings(target) {
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}blackbox/findings/combined`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgID, token, target }),
      });
      const res = await response.json();
      const list = res && res.status && Array.isArray(res.data) ? res.data : [];

      const rawFindings = list.map((f) => ({
        ...f,
        _index: f.findingIndex,
        _taskDataId: f.taskDataId,
        _rowKey: `${f.taskDataId}_${f.findingIndex}`,
      }));
      setFindings(rawFindings);

      const newCounts = { Critical: 0, High: 0, Medium: 0, Low: 0, Informational: 0 };
      rawFindings.forEach((item) => {
        const sev = item.Severity || item.Risk || "Informational";
        const matchKey = Object.keys(newCounts).find(
          (k) => k.toLowerCase() === String(sev).toLowerCase()
        );
        if (matchKey) {
          newCounts[matchKey]++;
        } else {
          newCounts.Informational++;
        }
      });
      setCounts(newCounts);
    } catch (err) {
      console.error("[ExposedBlackBox] Error fetching combined findings:", err);
      setFindings([]);
      setCounts(DEFAULT_COUNTS);
    }
  }

  // Fetch past scans (completed / killed / failed) for the Scan History modal
  async function fetchScanHistory() {
    const target = latestScanInfo?.target || selectedScope?.target;
    if (!token || !target) {
      setScanHistory([]);
      return;
    }
    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}blackbox/scans/history`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgID, token, target }),
      });
      const res = await response.json();
      setScanHistory(res && res.status && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("[ExposedBlackBox] Error fetching scan history:", err);
      setScanHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }

  const openScanHistory = () => {
    // Centre the modal on the content area, not the whole window, when the left sidebar is visible
    const sidebar = document.querySelector(".vertical-menu");
    const sidebarRight = sidebar && window.innerWidth >= 992 ? sidebar.getBoundingClientRect().right : 0;
    setHistorySidebarOffset(Math.max(0, Math.round(sidebarRight)));
    setModalScanHistory(true);
    fetchScanHistory();
  };

  const selectedScope =scopes.find((s) => s._id === selectedScopeId) || null;

  const activeTarget =
    selectedScope?.target ||
    (latestScanInfo?.target && latestScanInfo.target !== "scanme.nmap.org" ? latestScanInfo.target : null) ||
    (verifiedAssets.length > 0 ? verifiedAssets[0].target : null) ||
    localStorage.getItem("domain") ||
    "amenify.com";



  const handleScopeChange = (e) => {
    const scopeId = e.target.value;
    setSelectedScopeId(scopeId);
    const scopeObj = scopes.find((s) => s._id === scopeId);
    if (scopeObj) {
      const defaultType = scopeObj.scanTypesAllowed?.[0] || "unauthenticated";
      setSelectedScanType(defaultType);
      const safeTarget = (scopeObj.target || "").replace(/\./g, "_");
      setScanName(`BlackBox-${safeTarget}`);
      if (!scopeObj.vaptAllowed && selectedKind === "vapt") {
        setSelectedKind("va");
      }
    }
  };

  // Create new authorized scope
  async function handleCreateScopeSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!newScopeTarget) {
      setFeedback({ type: "danger", message: "Please select a verified asset target" });
      return;
    }
    if (newScopeScanTypes.length === 0) {
      setFeedback({ type: "danger", message: "At least one allowed scan type must be selected" });
      return;
    }

    setIsCreatingScope(true);
    setFeedback(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}blackbox/scope/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orgID,
          token,
          target: newScopeTarget,
          targetType: newScopeTargetType,
          scanTypesAllowed: newScopeScanTypes,
          intrusiveAllowed: newScopeIntrusive,
          vaptAllowed: newScopeVapt,
          testingWindowStart: newScopeWindowStart || null,
          testingWindowEnd: newScopeWindowEnd || null,
          expiresAt: newScopeExpiry ? new Date(newScopeExpiry) : null,
          rateLimit: Number(newScopeRateLimit) || 1,
        }),
      });

      const res = await response.json();
      if (response.ok && res.status) {
        setFeedback({
          type: "success",
          message: `Scope successfully authorized for ${res.scope.target}!`,
        });
        setModalCreateScope(false);
        setNewScopeVapt(false);
        setNewScopeIntrusive(false);
        await fetchScopes();
        setSelectedScopeId(res.scope._id);
        setSelectedScanType(res.scope.scanTypesAllowed[0] || "unauthenticated");
        setSelectedKind("va");
        setScanName(`BlackBox-${res.scope.target}`);
      } else {
        setFeedback({
          type: "danger",
          message: res.error || "Failed to authorize scope",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsCreatingScope(false);
    }
  }

  // Revoke scope
  async function handleRevokeScope(scopeId) {
    if (!window.confirm("Are you sure you want to revoke authorization for this scope?")) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}blackbox/scope/revoke`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ scopeId, orgID, token }),
      });
      const res = await response.json();
      if (res.status) {
        setFeedback({ type: "success", message: "Scope revoked successfully" });
        await fetchScopes();
      } else {
        setFeedback({ type: "danger", message: res.error || "Failed to revoke scope" });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    }
  }

  // Trigger scan
  async function handleTriggerScan(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedScopeId) {
      setFeedback({
        type: "danger",
        message: "An authorized scope must be selected before launching a scan.",
      });
      return;
    }

    setIsTriggering(true);
    setFeedback(null);

    try {
      const rawTargetClean = (selectedScope?.target || "").replace(/\./g, "_");
      const chosenName = scanName.trim() || `BlackBox-${rawTargetClean}`;
      const safeScanName = chosenName
        .replace(/\./g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/_+/g, "_")
        .replace(/^[-_]+|[-_]+$/g, "") || `BlackBox-${rawTargetClean}`;

      const credentials = {};
      if (selectedScanType === "auth_ssh") {
        credentials.username = credUsername.trim() || "root";
        credentials.password = credPassword;
        credentials.port = Number(credPort) || 22;
      } else if (selectedScanType === "auth_ssh_key") {
        credentials.username = credUsername.trim() || "root";
        credentials.port = Number(credPort) || 22;
        credentials.privateKey = credKey;
      } else if (selectedScanType === "auth_smb") {
        credentials.username = credUsername.trim() || "Administrator";
        credentials.password = credPassword;
        credentials.domain = credDomain.trim();
      }

      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}blackbox/trigger-scan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orgID,
          token,
          scopeId: selectedScopeId,
          scanType: selectedScanType,
          kind: selectedKind,
          scan_name: safeScanName,
          company_name: companyName.trim() || "Glogix",
          credentials,
        }),
      });

      const res = await response.json();
      if (response.ok && res.status) {
        setFeedback({
          type: "success",
          message: `Scan successfully launched for ${selectedScope?.target}! Initializing assessment...`,
        });
        setScanStatus("running");
        setTimeout(() => fetchTaskData(), 2000);
      } else {
        setFeedback({
          type: "danger",
          message: res.error || "Scan rejected by safety governance check",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsTriggering(false);
    }
  }

  // Emergency Kill Scan
  async function handleKillScan() {
    if (!latestTaskId) return;
    if (!window.confirm("Emergency Kill Switch: Are you sure you want to abort the currently running scan immediately?")) return;

    setIsKilling(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/scan/${latestTaskId}/kill`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ orgID, token, taskDataId: latestTaskId }),
        }
      );

      const res = await response.json();
      if (response.ok && res.status) {
        setFeedback({ type: "warning", message: "Scan killed successfully by user request." });
        setScanStatus("killed");
        fetchTaskData();
      } else {
        setFeedback({ type: "danger", message: res.error || "Failed to abort scan" });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsKilling(false);
    }
  }

  // Download PDF Report (latest scan from the Scan card)
  async function handleDownloadReport() {
    if (!latestTaskId) return;
    setIsDownloadingReport(true);
    try {
      await downloadScanReport(latestTaskId, latestScanInfo);
    } finally {
      setIsDownloadingReport(false);
    }
  }

  // Download PDF Report for a specific scan from the Scan History modal
  async function handleDownloadHistoryReport(scan) {
    setDownloadingTaskId(scan.taskDataId);
    try {
      await downloadScanReport(scan.taskDataId, scan);
    } finally {
      setDownloadingTaskId(null);
    }
  }

  async function downloadScanReport(taskId, scanInfo) {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/scan/${taskId}/report?orgID=${orgID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
        }
      );

      if (!response.ok) {
        let errMsg = "Failed to download report";
        try {
          const errJson = await response.json();
          if (errJson.error) errMsg = errJson.error;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const disposition = response.headers.get("content-disposition");
      let filename = `XAI_Scan_Report_${scanInfo?.target || "blackbox"}_${scanInfo?.scanId || taskId}.pdf`;
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (match && match[1]) filename = match[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setFeedback({ type: "success", message: "PDF report downloaded successfully." });
    } catch (err) {
      setFeedback({ type: "danger", message: err.message || "Failed to download PDF report" });
    }
  }

  // Rescan
  async function handleRescan() {
    if (!latestTaskId) return;
    if (!window.confirm("Are you sure you want to trigger a rescan for this target?")) return;

    setIsRescanning(true);
    setFeedback(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/scan/${latestTaskId}/rescan`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ orgID, token }),
        }
      );

      const res = await response.json();
      if (response.ok && res.status) {
        setFeedback({
          type: "success",
          message: "Rescan initiated successfully! Assessment is running...",
        });
        setScanStatus("running");
        setTimeout(() => fetchTaskData(), 2000);
      } else {
        setFeedback({
          type: "danger",
          message: res.error || "Failed to trigger rescan",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message });
    } finally {
      setIsRescanning(false);
    }
  }

  // Copy Scan ID to clipboard
  const handleCopyScanId = () => {
    const idToCopy = latestScanInfo?.scanId || latestTaskId || "";
    if (!idToCopy) return;
    try {
      navigator.clipboard.writeText(idToCopy);
      setCopiedId(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedId(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Update Finding Status
  // Rows in the combined table come from different scans, so each uses its own source taskDataId
  async function handleUpdateFindingStatus(finding, newStatus) {
    const taskId = finding?._taskDataId || latestTaskId;
    const findingIndex = finding?._index;
    if (!taskId || findingIndex === undefined || findingIndex === null) return;
    const rowKey = finding._rowKey;
    setUpdatingFindingIndex(rowKey);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}blackbox/finding/${taskId}/${findingIndex}/status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orgID,
            token,
            status: newStatus,
          }),
        }
      );
      const res = await response.json();
      if (response.ok && res?.status) {
        setFindings((prev) =>
          prev.map((f) => (f._rowKey === rowKey ? { ...f, status: newStatus } : f))
        );
        if (drawerFinding && drawerFinding._rowKey === rowKey) {
          setDrawerFinding((prev) => ({ ...prev, status: newStatus }));
        }
        setFeedback({
          type: "success",
          message: `Finding status updated to "${newStatus}"`,
        });
      } else {
        setFeedback({
          type: "danger",
          message: res?.error || res?.message || "Failed to update finding status",
        });
      }
    } catch (err) {
      setFeedback({ type: "danger", message: err.message || "An unexpected error occurred" });
    } finally {
      setUpdatingFindingIndex(null);
    }
  }

  // Calculate Health/Risk Score
  const hasScanned = !!latestScanInfo || (Array.isArray(findings) && findings.length > 0);
  const healthScore = Math.max(
    0,
    100 - counts.Critical * 25 - counts.High * 15 - counts.Medium * 5 - counts.Low * 2
  );
  const riskScorePercentage = hasScanned ? healthScore : 100;

  // SVG Gauge calculations (from HTML template: 2 * Math.PI * 82 = 515.22, 75% track = 386.42)
  const gaugeRadius = 82;
  const gaugeCircumference = 2 * Math.PI * gaugeRadius;
  const gaugeTrackLength = gaugeCircumference * 0.75; // 270 degrees sweep
  const gaugeValueLength = hasScanned ? gaugeTrackLength * (riskScorePercentage / 100) : 0;

  // Maximum finding count for the horizontal bar breakdown
  const maxFindingCount = Math.max(
    1,
    counts.Critical,
    counts.High,
    counts.Medium,
    counts.Low,
    counts.Informational
  );

  // Active scopes count
  const activeScopes = scopes.filter((s) => s.status === "active");

  // Filter & Sort findings for table
  let filteredFindings = [...findings];

  // Filter by severity if tile clicked
  if (selectedSeverityFilter) {
    const cfg = SEVERITY_CONFIGS.find((c) => c.key === selectedSeverityFilter);
    if (cfg) {
      filteredFindings = filteredFindings.filter((item) => {
        const s = String(item.Severity || item.Risk || "Informational").toLowerCase();
        return cfg.match.some((m) => s === m);
      });
    }
  }

  // Filter by search query
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    filteredFindings = filteredFindings.filter((item) => {
      const idStr = String(item.ID || "").toLowerCase();
      const titleStr = String(item.Title || "").toLowerCase();
      const hostStr = String(item.Host || latestScanInfo?.target || "").toLowerCase();
      const portStr = String(item.Port || "").toLowerCase();
      const sevStr = String(item.Severity || item.Risk || "").toLowerCase();
      const cveStr = String(item.CVE || item.cve || "").toLowerCase();
      return (
        idStr.includes(q) ||
        titleStr.includes(q) ||
        hostStr.includes(q) ||
        portStr.includes(q) ||
        sevStr.includes(q) ||
        cveStr.includes(q)
      );
    });
  }

  // Sort
  if (sortKey) {
    filteredFindings.sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];

      if (sortKey === "Severity") {
        const rank = { critical: 5, high: 4, medium: 3, med: 3, low: 2, informational: 1, info: 1 };
        const rA = rank[String(a.Severity || a.Risk || "").toLowerCase()] || 0;
        const rB = rank[String(b.Severity || b.Risk || "").toLowerCase()] || 0;
        return (rA - rB) * sortDir;
      }

      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";
      return String(valA).localeCompare(String(valB)) * sortDir;
    });
  }

  // Paginated findings
  const totalPages = Math.max(1, Math.ceil(filteredFindings.length / pageSize));
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedFindings = filteredFindings.slice(startIdx, startIdx + pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  const getSeverityBadgeClass = (severity) => {
    const s = String(severity || "informational").toLowerCase();
    if (s === "critical") return "sev-badge sev-critical";
    if (s === "high") return "sev-badge sev-high";
    if (s === "medium" || s === "med") return "sev-badge sev-medium";
    if (s === "low") return "sev-badge sev-low";
    return "sev-badge sev-info";
  };

  const getSeverityLeftBorder = (severity) => {
    const s = String(severity || "informational").toLowerCase();
    if (s === "critical") return "var(--sev-critical)";
    if (s === "high") return "var(--sev-high)";
    if (s === "medium" || s === "med") return "var(--sev-medium)";
    if (s === "low") return "var(--sev-low)";
    return "var(--sev-info)";
  };

  // VA = blue, VAPT = purple (same colors as the Scan card's mode badge)
  const renderKindBadge = (kind) => {
    const isVapt = String(kind || "va").toLowerCase() === "vapt";
    return (
      <span
        style={{
          fontSize: "11px",
          fontFamily: "var(--font-mono)",
          fontWeight: "600",
          padding: "2px 6px",
          borderRadius: "var(--radius-sm)",
          background: isVapt ? "rgba(139, 92, 246, 0.2)" : "rgba(47, 111, 237, 0.15)",
          color: isVapt ? "var(--purple-400)" : "var(--brand-primary)",
          border: "1px solid var(--border-default)",
          whiteSpace: "nowrap",
        }}
      >
        {isVapt ? "VAPT" : "VA"}
      </span>
    );
  };

  const formatScanDate = (value) => {
    if (!value) return "-";
    const dt = new Date(value);
    return isNaN(dt.getTime()) ? String(value) : dt.toLocaleString();
  };

  return (
    <div className="blackbox-page-root" data-theme={theme}>
      <div className="blackbox-container">
        {/* Breadcrumb & Title */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div className="blackbox-breadcrumb">
            <Link to="/dashboard">Dashboard</Link>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span>X-AI VA Governance</span>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span className="current">Black Box Findings</span>
          </div>
          <h1 className="blackbox-title">Black Box Findings</h1>
        </div>

        {/* Global Feedback Alert */}
        {feedback && (
          <Alert color={feedback.type} isOpen={true} toggle={() => setFeedback(null)} className="mb-0">
            {feedback.message}
          </Alert>
        )}

        {/* Card 1: Authorized Target Governance & Scan Dispatch */}
        <section className="blackbox-card">
          <div
            style={{
              padding: "16px 20px",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
              borderBottom: "1px solid var(--border-default)",
            }}
          >
            <div style={{ display: "flex", gap: "12px", flex: "1 1 420px", minWidth: 0 }}>
              <span
                style={{
                  width: "36px",
                  height: "36px",
                  flexShrink: 0,
                  borderRadius: "var(--radius-md)",
                  background: "var(--slate-800)",
                  color: "var(--blue-500)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 13c0 5-3.5 7.5-7.7 9a1 1 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.2-2.7a1.2 1.2 0 0 1 1.6 0C14.5 3.8 17 5 19 5a1 1 0 0 1 1 1z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </span>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
                <span style={{ fontSize: "15px", fontWeight: "600", color: "var(--fg-primary)" }}>
                  Authorized Target Governance &amp; Scan Dispatch
                </span>
                <span style={{ fontSize: "13px", color: "var(--fg-secondary)" }}>
                  In compliance with safety controls, scans can only be launched against{" "}
                  <b style={{ color: "var(--fg-primary)", fontWeight: "600" }}>verified assets</b> in your Asset Inventory with an active authorization scope.
                </span>
              </div>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              <button
                className="blackbox-btn"
                onClick={() => setModalManageScopes(true)}
              >
                Manage Scopes
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "11px",
                    padding: "0 6px",
                    borderRadius: "999px",
                    background: "var(--slate-800)",
                    color: "var(--fg-secondary)",
                  }}
                >
                  {scopes.length}
                </span>
              </button>
              {canManageScope && (
                <button
                  className="blackbox-btn btn-primary"
                  onClick={() => {
                    fetchVerifiedAssets();
                    setModalCreateScope(true);
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Authorize New Scope
                </button>
              )}
            </div>
          </div>

          <div style={{ padding: "20px" }}>
            {activeScopes.length === 0 ? (
              <div
                style={{
                  border: "1px dashed var(--border-strong)",
                  borderRadius: "var(--radius-lg)",
                  background: "var(--bg-surface-muted)",
                  padding: "28px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                  textAlign: "center",
                }}
              >
                <span
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    background: "var(--amber-100)",
                    color: "var(--amber-600)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3" />
                    <path d="M12 9v4M12 17h.01" />
                  </svg>
                </span>
                <span style={{ fontSize: "15px", fontWeight: "600", color: "var(--fg-primary)" }}>
                  No Active Authorized Scopes Found
                </span>
                <span style={{ fontSize: "13px", color: "var(--fg-secondary)", maxWidth: "560px" }}>
                  Before launching a black-box vulnerability scan, you must define and authorize an asset scope from your verified Asset Inventory.
                </span>
                {canManageScope ? (
                  <button
                    className="blackbox-btn btn-primary"
                    style={{ marginTop: "6px", height: "36px", padding: "0 16px" }}
                    onClick={() => {
                      fetchVerifiedAssets();
                      setModalCreateScope(true);
                    }}
                  >
                    Authorize Target Scope
                  </button>
                ) : (
                  <span style={{ fontSize: "12px", color: "var(--fg-secondary)", marginTop: "6px", fontStyle: "italic" }}>
                    Please contact an Organization Admin to define and authorize target scopes.
                  </span>
                )}
              </div>
            ) : (
              <AvForm onValidSubmit={handleTriggerScan}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px", alignItems: "flex-end" }}>
                  <div>
                    <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                      Select Authorized Scope
                    </Label>
                    <select
                      className="form-select"
                      value={selectedScopeId}
                      onChange={handleScopeChange}
                      style={{
                        height: "36px",
                        background: "var(--bg-surface-sunken)",
                        color: "var(--fg-primary)",
                        borderColor: "var(--border-strong)",
                        borderRadius: "var(--radius-md)",
                        fontSize: "13px",
                      }}
                      required
                    >
                      {activeScopes.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.target} ({s.targetType.toUpperCase()}) — Max Concurrency: {s.rateLimit}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                      Scan Type
                    </Label>
                    <select
                      className="form-select"
                      value={selectedScanType}
                      onChange={(e) => setSelectedScanType(e.target.value)}
                      style={{
                        height: "36px",
                        background: "var(--bg-surface-sunken)",
                        color: "var(--fg-primary)",
                        borderColor: "var(--border-strong)",
                        borderRadius: "var(--radius-md)",
                        fontSize: "13px",
                      }}
                      required
                    >
                      {(selectedScope?.scanTypesAllowed || ["unauthenticated"]).map((st) => {
                        const meta = ALL_SCAN_TYPES.find((m) => m.id === st);
                        return (
                          <option key={st} value={st}>
                            {meta ? meta.label : st}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div>
                    <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                      Assessment Mode
                    </Label>
                    <select
                      className="form-select"
                      value={selectedKind}
                      onChange={(e) => setSelectedKind(e.target.value)}
                      style={{
                        height: "36px",
                        background: "var(--bg-surface-sunken)",
                        color: "var(--fg-primary)",
                        borderColor: "var(--border-strong)",
                        borderRadius: "var(--radius-md)",
                        fontSize: "13px",
                      }}
                      required
                    >
                      <option value="va">VA — Vulnerability Assessment</option>
                      <option
                        value="vapt"
                        disabled={!selectedScope?.vaptAllowed}
                      >
                        VAPT — Penetration Testing {!selectedScope?.vaptAllowed ? "(Scope Restricted)" : ""}
                      </option>
                    </select>
                  </div>

                  <div>
                    <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                      Scan Name
                    </Label>
                    <input
                      type="text"
                      name="scanName"
                      value={scanName}
                      onChange={(e) => setScanName(e.target.value)}
                      placeholder="BlackBox-Scan"
                      className="form-control"
                      style={{
                        height: "36px",
                        background: "var(--bg-surface-sunken)",
                        color: "var(--fg-primary)",
                        borderColor: "var(--border-strong)",
                        borderRadius: "var(--radius-md)",
                        fontSize: "13px",
                      }}
                    />
                  </div>

                  <div>
                    <button
                      type="submit"
                      className="blackbox-btn btn-primary"
                      style={{ width: "100%", height: "36px" }}
                      disabled={isTriggering || scanStatus === "running" || activeScopes.length === 0 || !canRunScan}
                      title={!canRunScan ? "Read-only access: Launching scans requires Analyst or Admin role" : undefined}
                    >
                      {isTriggering ? (
                        <>
                          <Spinner size="sm" className="me-1" /> Launching...
                        </>
                      ) : scanStatus === "running" ? (
                        "Scan In Progress"
                      ) : !canRunScan ? (
                        "Scan Restricted (Read Only)"
                      ) : (
                        "Start Scan"
                      )}
                    </button>
                  </div>
                </div>

                {selectedScanType !== "unauthenticated" && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px 14px",
                      borderRadius: "var(--radius-md)",
                      background: "var(--bg-surface-sunken)",
                      border: "1px solid var(--border-default)",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "12px",
                    }}
                  >
                    <div>
                      <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                        Credential Username
                      </Label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={selectedScanType === "auth_smb" ? "Administrator" : "root"}
                        value={credUsername}
                        onChange={(e) => setCredUsername(e.target.value)}
                        style={{ height: "34px", fontSize: "12px", background: "var(--bg-surface)", color: "var(--fg-primary)", borderColor: "var(--border-strong)" }}
                      />
                    </div>

                    {selectedScanType === "auth_ssh_key" ? (
                      <div style={{ gridColumn: "span 2" }}>
                        <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                          SSH Private Key Content
                        </Label>
                        <textarea
                          rows={2}
                          className="form-control font-monospace"
                          placeholder="-----BEGIN OPENSSH PRIVATE KEY-----..."
                          value={credKey}
                          onChange={(e) => setCredKey(e.target.value)}
                          style={{ fontSize: "11px", background: "var(--bg-surface)", color: "var(--fg-primary)", borderColor: "var(--border-strong)" }}
                        />
                      </div>
                    ) : (
                      <div>
                        <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                          Credential Password
                        </Label>
                        <input
                          type="password"
                          className="form-control"
                          placeholder="Password"
                          value={credPassword}
                          onChange={(e) => setCredPassword(e.target.value)}
                          style={{ height: "34px", fontSize: "12px", background: "var(--bg-surface)", color: "var(--fg-primary)", borderColor: "var(--border-strong)" }}
                        />
                      </div>
                    )}

                    {selectedScanType === "auth_smb" && (
                      <div>
                        <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                          Domain (optional)
                        </Label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="WORKGROUP"
                          value={credDomain}
                          onChange={(e) => setCredDomain(e.target.value)}
                          style={{ height: "34px", fontSize: "12px", background: "var(--bg-surface)", color: "var(--fg-primary)", borderColor: "var(--border-strong)" }}
                        />
                      </div>
                    )}

                    {(selectedScanType === "auth_ssh" || selectedScanType === "auth_ssh_key") && (
                      <div>
                        <Label className="small fw-semibold mb-1" style={{ color: "var(--fg-secondary)" }}>
                          SSH Port
                        </Label>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="22"
                          value={credPort}
                          onChange={(e) => setCredPort(e.target.value)}
                          style={{ height: "34px", fontSize: "12px", background: "var(--bg-surface)", color: "var(--fg-primary)", borderColor: "var(--border-strong)" }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {selectedScope && (
                  <div style={{ marginTop: "10px", display: "flex", gap: "6px", flexWrap: "wrap", fontSize: "11px", color: "var(--fg-secondary)" }}>
                    <span style={{ padding: "2px 8px", borderRadius: "var(--radius-sm)", background: "var(--slate-800)", border: "1px solid var(--border-default)" }}>
                      Asset ID: {selectedScope.assetId}
                    </span>
                    {selectedScope.testingWindowStart && (
                      <span style={{ padding: "2px 8px", borderRadius: "var(--radius-sm)", background: "rgba(47, 111, 237, 0.2)", color: "var(--blue-500)" }}>
                        Window: {selectedScope.testingWindowStart} - {selectedScope.testingWindowEnd}
                      </span>
                    )}
                    {selectedScope.intrusiveAllowed && (
                      <span style={{ padding: "2px 8px", borderRadius: "var(--radius-sm)", background: "rgba(220, 59, 44, 0.2)", color: "var(--red-500)" }}>
                        Intrusive Enabled
                      </span>
                    )}
                  </div>
                )}
              </AvForm>
            )}
          </div>
        </section>

        {/* Card 2: Scan Overview (Target Host, Scan ID with Copy, Scan Type, Authorized By, Actions) */}
        <section className="blackbox-card">
          <div className="blackbox-card-header">
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: "var(--fg-primary)" }}>Scan</span>
              <span
                className={`blackbox-status-pill ${
                  scanStatus === "completed"
                    ? "status-completed"
                    : scanStatus === "running"
                    ? "status-running"
                    : scanStatus === "killed"
                    ? "status-killed"
                    : scanStatus === "failed"
                    ? "status-failed"
                    : "status-idle"
                }`}
              >
                <span className="dot"></span>
                {scanStatus === "running"
                  ? `running (${latestScanInfo?.progress ?? 0}%)`
                  : scanStatus === "completed"
                  ? "completed"
                  : scanStatus === "killed"
                  ? "killed"
                  : scanStatus === "failed"
                  ? "failed"
                  : "idle"}
              </span>
            </div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {scanStatus === "running" && canRunScan && (
                <button
                  className="blackbox-btn btn-danger"
                  onClick={handleKillScan}
                  disabled={isKilling}
                >
                  {isKilling ? <Spinner size="sm" /> : "🛑 Emergency Kill Scan"}
                </button>
              )}
              <button
                className="blackbox-btn"
                onClick={openScanHistory}
                disabled={!(latestScanInfo?.target || selectedScope?.target)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                  <path d="M12 7v5l4 2" />
                </svg>
                View Scan History
              </button>
              <button
                className="blackbox-btn"
                onClick={fetchTaskData}
                disabled={scanStatus === "running" && isTriggering}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M21 3v5h-5" />
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                  <path d="M8 16H3v5" />
                </svg>
                Refresh
              </button>
              {canRunScan && (
                <button
                  className="blackbox-btn"
                  onClick={handleRescan}
                  disabled={isRescanning || !latestTaskId}
                >
                  {isRescanning ? (
                    <Spinner size="sm" />
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-9-9c2.5 0 4.8 1 6.5 2.7L21 8" />
                      <path d="M21 3v5h-5" />
                    </svg>
                  )}
                  Rescan
                </button>
              )}
              <button
                className="blackbox-btn btn-primary"
                onClick={handleDownloadReport}
                disabled={isDownloadingReport || !latestTaskId}
              >
                {isDownloadingReport ? (
                  <Spinner size="sm" />
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                )}
                Download Report (PDF)
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px", borderRight: "1px solid var(--border-default)" }}>
              <span style={{ fontSize: "11px", color: "var(--fg-secondary)" }}>Target Host</span>
              <span style={{ fontSize: "14px", fontWeight: "600", fontFamily: "var(--font-mono)", color: "var(--fg-primary)" }}>
                {latestScanInfo?.target || selectedScope?.target || (verifiedAssets.length > 0 ? verifiedAssets[0].target : "—")}
              </span>
            </div>

            <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px", borderRight: "1px solid var(--border-default)", gridColumn: "span 2", minWidth: 0 }}>
              <span style={{ fontSize: "11px", color: "var(--fg-secondary)" }}>Scan ID</span>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                <span style={{ fontSize: "12px", fontFamily: "var(--font-mono)", color: latestScanInfo?.scanId || latestTaskId ? "var(--blue-500)" : "var(--fg-tertiary)", overflowWrap: "anywhere" }}>
                  {latestScanInfo?.scanId || (latestTaskId ? `BlackBox_${(activeTarget || "").replace(/[^a-zA-Z0-9]/g, "_")}_${latestTaskId}` : "—")}
                </span>
                {Boolean(latestScanInfo?.scanId || latestTaskId) && (
                  <button
                    onClick={handleCopyScanId}
                    title={copiedId ? "Copied!" : "Copy Scan ID"}
                    aria-label="Copy Scan ID"
                    style={{
                      width: "26px",
                      height: "26px",
                      flexShrink: 0,
                      border: "1px solid var(--border-default)",
                      borderRadius: "var(--radius-sm)",
                      background: copiedId ? "rgba(31, 157, 87, 0.2)" : "transparent",
                      color: copiedId ? "var(--status-success)" : "var(--fg-secondary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    {copiedId ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="8" y="8" width="14" height="14" rx="2" />
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px", borderRight: "1px solid var(--border-default)" }}>
              <span style={{ fontSize: "11px", color: "var(--fg-secondary)" }}>Scan Type / Mode</span>
              <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: "12px",
                    fontFamily: "var(--font-mono)",
                    padding: "2px 8px",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--slate-800)",
                    border: "1px solid var(--border-default)",
                    color: "var(--fg-primary)",
                  }}
                >
                  {latestScanInfo?.scanType || selectedScope?.scanTypesAllowed?.[0] || selectedScanType || "unauthenticated"}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    fontFamily: "var(--font-mono)",
                    fontWeight: "600",
                    padding: "2px 6px",
                    borderRadius: "var(--radius-sm)",
                    background: String(latestScanInfo?.kind || selectedKind).toLowerCase() === "vapt" ? "rgba(139, 92, 246, 0.2)" : "rgba(47, 111, 237, 0.15)",
                    color: String(latestScanInfo?.kind || selectedKind).toLowerCase() === "vapt" ? "var(--purple-400)" : "var(--brand-primary)",
                    border: "1px solid var(--border-default)",
                    textTransform: "uppercase",
                  }}
                >
                  {latestScanInfo?.kind || selectedKind || "va"}
                </span>
              </div>
            </div>

            <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={{ fontSize: "11px", color: "var(--fg-secondary)" }}>Authorized By</span>
              <span style={{ fontSize: "13px", fontFamily: "var(--font-mono)", color: "var(--fg-primary)", overflowWrap: "anywhere" }}>
                {latestScanInfo?.triggeredBy || selectedScope?.authorizedBy || (hasScanned ? (userEmail || "—") : "—")}
              </span>
            </div>
          </div>

          {scanStatus === "running" && (
            <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-default)", background: "var(--bg-surface-sunken)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "12px" }}>
                <span style={{ color: "var(--fg-secondary)" }}>Scan Execution Progress</span>
                <span style={{ fontFamily: "var(--font-mono)", fontWeight: "600", color: "var(--brand-primary)" }}>
                  {latestScanInfo?.progress ?? 0}%
                </span>
              </div>
              <div style={{ height: "6px", borderRadius: "3px", background: "var(--bg-surface-muted)", overflow: "hidden" }}>
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, latestScanInfo?.progress ?? 0))}%`,
                    height: "100%",
                    borderRadius: "3px",
                    background: "var(--brand-primary)",
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
            </div>
          )}
        </section>

        {/* Row 3: Risk Score Gauge + Severity Count Tiles (Left) & Severity Breakdown (Right) */}
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "stretch" }}>
          {/* Card Left: Gauge + 5 Severity tiles */}
          <section
            className="blackbox-card"
            style={{ flex: "1 1 520px", minWidth: 0, display: "flex", flexWrap: "wrap" }}
          >
            {/* Gauge Circle */}
            <div
              style={{
                flex: "0 0 auto",
                padding: "20px 24px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRight: "1px solid var(--border-default)",
              }}
            >
              <div style={{ position: "relative", width: "140px", height: "140px" }}>
                <svg width="140" height="140" viewBox="0 0 200 200">
                  <circle
                    cx="100"
                    cy="100"
                    r={gaugeRadius}
                    fill="none"
                    stroke="var(--slate-800)"
                    strokeWidth="14"
                    strokeLinecap="round"
                    strokeDasharray={`${gaugeTrackLength} ${gaugeCircumference}`}
                    transform="rotate(135 100 100)"
                  />
                  <circle
                    cx="100"
                    cy="100"
                    r={gaugeRadius}
                    fill="none"
                    stroke={
                      !hasScanned
                        ? "var(--slate-700)"
                        : counts.Critical > 0
                        ? "var(--sev-critical)"
                        : counts.High > 0
                        ? "var(--sev-high)"
                        : counts.Medium > 0
                        ? "var(--sev-medium)"
                        : "var(--status-success)"
                    }
                    strokeWidth="14"
                    strokeLinecap="round"
                    strokeDasharray={`${hasScanned ? gaugeValueLength : 0} ${gaugeCircumference}`}
                    transform="rotate(135 100 100)"
                    style={{ transition: "stroke-dasharray 0.6s ease" }}
                  />
                </svg>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: "32px", fontWeight: "700", letterSpacing: "-0.02em", color: "var(--fg-primary)" }}>
                    {hasScanned ? `${riskScorePercentage}%` : "—"}
                  </span>
                  <span style={{ fontSize: "11px", color: "var(--fg-secondary)", marginTop: "-2px" }}>
                    {hasScanned ? "Score" : "Not Scanned"}
                  </span>
                </div>
              </div>
            </div>

            {/* Severity Count Tiles */}
            <div
              style={{
                flex: "1 1 300px",
                padding: "20px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))",
                gap: "8px",
                alignContent: "center",
              }}
            >
              {SEVERITY_CONFIGS.map((cfg) => {
                const countVal = counts[cfg.label] || 0;
                const isSelected = selectedSeverityFilter === cfg.key;
                return (
                  <button
                    key={cfg.key}
                    onClick={() => {
                      setSelectedSeverityFilter((prev) => (prev === cfg.key ? null : cfg.key));
                      setCurrentPage(1);
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      padding: "12px",
                      borderRadius: "var(--radius-md)",
                      border: isSelected ? "1px solid var(--brand-primary)" : "1px solid var(--border-default)",
                      borderTop: `3px solid ${cfg.color}`,
                      background: isSelected
                        ? "rgba(47, 111, 237, 0.12)"
                        : countVal > 0
                        ? "var(--bg-surface)"
                        : "var(--bg-surface-muted)",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: "var(--font-ui)",
                      color: "var(--fg-primary)",
                      transition: "border-color 0.15s ease, background 0.15s ease",
                    }}
                  >
                    <span style={{ fontSize: "12px", color: "var(--fg-secondary)", whiteSpace: "nowrap" }}>
                      {cfg.label}
                    </span>
                    <span
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        lineHeight: 1,
                        color: countVal > 0 ? "var(--fg-primary)" : "var(--fg-tertiary)",
                      }}
                    >
                      {countVal}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Card Right: Findings Severity Breakdown */}
          <section
            className="blackbox-card"
            style={{ flex: "1 1 380px", minWidth: 0, display: "flex", flexDirection: "column" }}
          >
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-default)", fontSize: "13px", fontWeight: "600", color: "var(--fg-primary)" }}>
              Findings Severity Breakdown
            </div>
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "12px", flex: 1, justifyContent: "center" }}>
              {SEVERITY_CONFIGS.map((cfg) => {
                const countVal = counts[cfg.label] || 0;
                const pct = maxFindingCount > 0 ? (countVal / maxFindingCount) * 100 : 0;
                return (
                  <div
                    key={cfg.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "100px minmax(0, 1fr) 24px",
                      alignItems: "center",
                      gap: "12px",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setSelectedSeverityFilter((prev) => (prev === cfg.key ? null : cfg.key));
                      setCurrentPage(1);
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        color: countVal > 0 ? "var(--fg-primary)" : "var(--fg-secondary)",
                        fontWeight: selectedSeverityFilter === cfg.key ? "600" : "400",
                      }}
                    >
                      {cfg.label}
                    </span>
                    <div style={{ height: "14px", borderRadius: "3px", background: "var(--bg-surface-muted)", overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: "100%",
                          borderRadius: "3px",
                          background: cfg.color,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "13px",
                        fontWeight: "600",
                        textAlign: "right",
                        color: countVal > 0 ? "var(--fg-primary)" : "var(--fg-tertiary)",
                      }}
                    >
                      {countVal}
                    </span>
                  </div>
                );
              })}

              {/* Axis markings */}
              <div style={{ display: "grid", gridTemplateColumns: "100px minmax(0, 1fr) 24px", gap: "12px" }}>
                <span></span>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "10px", fontFamily: "var(--font-mono)", color: "var(--fg-tertiary)" }}>
                  <span>0</span>
                  <span>{Math.ceil(maxFindingCount / 2)}</span>
                  <span>{maxFindingCount}</span>
                </div>
                <span></span>
              </div>
            </div>
          </section>
        </div>

        {/* Card 4: Findings Table */}
        <section className="blackbox-card">
          <div
            style={{
              padding: "14px 16px",
              borderBottom: "1px solid var(--border-default)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--fg-primary)" }}>
                  Findings Table
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    fontFamily: "var(--font-mono)",
                    color: "var(--fg-secondary)",
                    background: "var(--slate-800)",
                    padding: "1px 7px",
                    borderRadius: "999px",
                  }}
                >
                  {filteredFindings.length} findings
                </span>
              </div>
              <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                Target: <b style={{ color: "var(--fg-primary)", fontWeight: "600" }}>{latestScanInfo?.target || selectedScope?.target || (verifiedAssets.length > 0 ? verifiedAssets[0].target : "—")}</b> · Status:{" "}
                <b style={{ color: "var(--fg-primary)", fontWeight: "600" }}>{latestScanInfo?.status || scanStatus}</b> · Triggered by:{" "}
                <b style={{ color: "var(--fg-primary)", fontWeight: "600" }}>{latestScanInfo?.triggeredBy || selectedScope?.authorizedBy || (hasScanned ? (userEmail || "—") : "—")}</b>
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {selectedSeverityFilter && (
                <button
                  onClick={() => setSelectedSeverityFilter(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    height: "30px",
                    padding: "0 10px",
                    border: "1px solid var(--brand-primary)",
                    borderRadius: "var(--radius-md)",
                    background: "rgba(47, 111, 237, 0.12)",
                    color: "var(--fg-primary)",
                    fontFamily: "var(--font-ui)",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  {SEVERITY_CONFIGS.find((c) => c.key === selectedSeverityFilter)?.label}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  width: "240px",
                  height: "30px",
                  padding: "0 10px",
                  border: "1px solid var(--border-strong)",
                  borderRadius: "var(--radius-md)",
                  background: "var(--bg-surface-sunken)",
                  boxSizing: "border-box",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Search"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: "var(--fg-primary)",
                    fontFamily: "var(--font-ui)",
                    fontSize: "12px",
                  }}
                />
              </div>
            </div>
          </div>

          <div className="blackbox-table-wrap">
            <table className="blackbox-table">
              <thead>
                <tr>
                  <th
                    className="sortable"
                    onClick={() => handleSort("ID")}
                    style={{ borderLeft: "3px solid transparent" }}
                  >
                    Finding ID {sortKey === "ID" && (sortDir === 1 ? "▲" : "▼")}
                  </th>
                  <th className="sortable" onClick={() => handleSort("Severity")}>
                    Severity {sortKey === "Severity" && (sortDir === 1 ? "▲" : "▼")}
                  </th>
                  <th className="sortable" onClick={() => handleSort("Title")} style={{ minWidth: "260px" }}>
                    Vulnerability / Finding {sortKey === "Title" && (sortDir === 1 ? "▲" : "▼")}
                  </th>
                  <th className="sortable" onClick={() => handleSort("Host")}>
                    Host / Target {sortKey === "Host" && (sortDir === 1 ? "▲" : "▼")}
                  </th>
                  <th className="sortable" onClick={() => handleSort("Port")}>
                    Port {sortKey === "Port" && (sortDir === 1 ? "▲" : "▼")}
                  </th>
                  <th className="sortable" onClick={() => handleSort("detectedVia")}>
                    Detected Via {sortKey === "detectedVia" && (sortDir === 1 ? "▲" : "▼")}
                  </th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFindings.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ textAlign: "center", padding: "48px 16px", color: "var(--fg-secondary)" }}>
                      {scanStatus === "running" ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                          <Spinner size="sm" color="primary" />
                          <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--fg-primary)" }}>
                            Scan in progress...
                          </span>
                          <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                            Reconnaissance assessment is actively running. Findings will appear here upon completion.
                          </span>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", padding: "24px 0" }}>
                          <div style={{ width: "42px", height: "42px", borderRadius: "50%", background: "var(--slate-800)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--fg-secondary)", marginBottom: "4px" }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                            </svg>
                          </div>
                          <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--fg-primary)" }}>
                            {searchQuery || selectedSeverityFilter ? "No matching findings" : "No Black-Box Scans Run Yet"}
                          </span>
                          <span style={{ fontSize: "12px", color: "var(--fg-secondary)", maxWidth: "460px" }}>
                            {searchQuery || selectedSeverityFilter
                              ? "Try resetting your search query or severity filters."
                              : `No vulnerability assessments have been recorded for ${activeTarget || "your domain"} yet. Authorize a scope above and click "Launch Black-Box Scan" to start assessment.`}
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedFindings.map((row, idx) => {
                    const findingSev = row.Severity || row.Risk || "Informational";
                    const findingIdentifier = row.ID || (row._index !== undefined ? row._index : idx);
                    const sourceTaskId = row._taskDataId || latestTaskId;
                    const detailUrl = sourceTaskId
                      ? `/exposedblackbox/finding/${sourceTaskId}/${findingIdentifier}`
                      : "#";
                    const isRowSelected = drawerFinding && drawerFinding._rowKey === row._rowKey;

                    return (
                      <tr
                        key={row._rowKey || idx}
                        style={{
                          background: isRowSelected ? "rgba(47, 111, 237, 0.08)" : undefined,
                        }}
                      >
                        <td
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: "12px",
                            color: "var(--fg-secondary)",
                            borderLeft: `3px solid ${getSeverityLeftBorder(findingSev)}`,
                          }}
                        >
                          {row.ID ? String(row.ID).substring(0, 14) + "..." : `FND-${row._index || idx}`}
                        </td>
                        <td>
                          <span className={getSeverityBadgeClass(findingSev)}>
                            {findingSev}
                          </span>
                        </td>
                        <td style={{ minWidth: "260px" }}>
                          <Link
                            to={detailUrl}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              fontWeight: findingSev.toLowerCase() === "critical" ? 600 : 500,
                              color: "var(--fg-primary)",
                            }}
                          >
                            <span>{row.Title || "Untitled Finding"}</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            </svg>
                          </Link>
                        </td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", fontWeight: "500", color: "var(--fg-primary)" }}>
                              {row.Host || latestScanInfo?.target || "-"}
                            </span>
                            <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>
                              {row.assetId ? `Linked to: ${row.assetName || row.Host}` : "Unlinked Asset"}
                            </span>
                          </div>
                        </td>
                        <td style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--fg-tertiary)" }}>
                          {row.Port || "-"}
                        </td>
                        <td title={row.scanId ? `Scan: ${row.scanId} · ${formatScanDate(row.scanCompletedAt)}` : undefined}>
                          {renderKindBadge(row.detectedVia)}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button
                            onClick={() => setDrawerFinding(row)}
                            style={{
                              height: "28px",
                              padding: "0 12px",
                              border: "1px solid var(--border-strong)",
                              borderRadius: "var(--radius-md)",
                              background: "var(--bg-surface)",
                              color: "var(--fg-primary)",
                              fontFamily: "var(--font-ui)",
                              fontSize: "12px",
                              fontWeight: "500",
                              cursor: "pointer",
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer / Pagination */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "10px 16px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "var(--fg-secondary)" }}>
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  height: "28px",
                  padding: "0 6px",
                  border: "1px solid var(--border-strong)",
                  borderRadius: "var(--radius-md)",
                  background: "var(--bg-surface)",
                  color: "var(--fg-primary)",
                  fontFamily: "var(--font-ui)",
                  fontSize: "12px",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>
                entries ·{" "}
                {filteredFindings.length
                  ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(
                      currentPage * pageSize,
                      filteredFindings.length
                    )} of ${filteredFindings.length} entries`
                  : "Showing 0 entries"}
              </span>
            </div>

            <div style={{ display: "flex", gap: "4px" }}>
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
                style={{
                  width: "28px",
                  height: "28px",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius-md)",
                  background: "transparent",
                  color: currentPage <= 1 ? "var(--fg-tertiary)" : "var(--fg-primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="m15 18-6-6 6-6" />
                </svg>
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .slice(Math.max(0, currentPage - 2), Math.min(totalPages, currentPage + 2))
                .map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    style={{
                      minWidth: "28px",
                      height: "28px",
                      border: "none",
                      borderRadius: "var(--radius-md)",
                      background: currentPage === pageNum ? "var(--brand-primary)" : "var(--bg-surface-muted)",
                      color: currentPage === pageNum ? "#fff" : "var(--fg-primary)",
                      fontFamily: "var(--font-ui)",
                      fontSize: "12px",
                      fontWeight: currentPage === pageNum ? "600" : "400",
                      cursor: "pointer",
                    }}
                  >
                    {pageNum}
                  </button>
                ))}
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
                style={{
                  width: "28px",
                  height: "28px",
                  border: "1px solid var(--border-default)",
                  borderRadius: "var(--radius-md)",
                  background: "transparent",
                  color: currentPage >= totalPages ? "var(--fg-tertiary)" : "var(--fg-primary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="m9 18 6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Slide-over Drawer for Finding Details */}
      {drawerFinding && (
        <>
          <div className="blackbox-drawer-backdrop" onClick={() => setDrawerFinding(null)} />
          <aside className="blackbox-drawer">
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border-default)", display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px", minWidth: 0 }}>
                <span className={getSeverityBadgeClass(drawerFinding.Severity || drawerFinding.Risk)}>
                  {drawerFinding.Severity || drawerFinding.Risk || "Informational"}
                </span>
                <span style={{ fontSize: "16px", fontWeight: "600", color: "var(--fg-primary)", lineHeight: "1.3" }}>
                  {drawerFinding.Title || "Finding Details"}
                </span>
              </div>
              <button
                onClick={() => setDrawerFinding(null)}
                aria-label="Close"
                style={{
                  width: "30px",
                  height: "30px",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  background: "transparent",
                  color: "var(--fg-secondary)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {[
                { k: "Finding ID", v: drawerFinding.ID || `FND-${drawerFinding._index}` },
                { k: "Severity", v: drawerFinding.Severity || drawerFinding.Risk || "Informational" },
                { k: "Host / Target", v: drawerFinding.Host || latestScanInfo?.target || "-" },
                { k: "Asset", v: drawerFinding.assetId ? `Linked to: ${drawerFinding.assetName || drawerFinding.Host}` : "Unlinked Asset" },
                { k: "Port", v: drawerFinding.Port || "-" },
                { k: "CVE Reference", v: drawerFinding.CVE || drawerFinding.cve || "-" },
                { k: "CVSS Score", v: drawerFinding.CVSS || drawerFinding.cvss || "-" },
                { k: "Detected Via", v: String(drawerFinding.detectedVia || "va").toUpperCase() },
                { k: "Source Scan", v: drawerFinding.scanId || "-" },
                { k: "Scan Completed", v: formatScanDate(drawerFinding.scanCompletedAt) },
              ].map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px minmax(0, 1fr)",
                    padding: "11px 20px",
                    borderBottom: "1px solid var(--border-default)",
                    fontSize: "13px",
                  }}
                >
                  <span style={{ color: "var(--fg-secondary)" }}>{f.k}</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--fg-primary)", overflowWrap: "anywhere" }}>
                    {f.v}
                  </span>
                </div>
              ))}

              {/* Status Workflow Selector */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "120px minmax(0, 1fr)",
                  padding: "11px 20px",
                  borderBottom: "1px solid var(--border-default)",
                  fontSize: "13px",
                  alignItems: "center",
                }}
              >
                <span style={{ color: "var(--fg-secondary)" }}>Workflow Status</span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <select
                    value={drawerFinding.status || "New"}
                    onChange={(e) => handleUpdateFindingStatus(drawerFinding, e.target.value)}
                    disabled={updatingFindingIndex === drawerFinding._rowKey}
                    style={{
                      height: "30px",
                      padding: "0 8px",
                      borderRadius: "var(--radius-sm)",
                      border: "1px solid var(--border-strong)",
                      background: "var(--bg-surface-sunken)",
                      color: "var(--fg-primary)",
                      fontSize: "12px",
                      fontFamily: "var(--font-ui)",
                    }}
                  >
                    {STATUS_OPTIONS.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                  {updatingFindingIndex === drawerFinding._rowKey && <Spinner size="sm" />}
                </div>
              </div>

              {/* Description & Solution Snippets */}
              {(drawerFinding.Description || drawerFinding.description) && (
                <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border-default)" }}>
                  <span style={{ fontSize: "12px", color: "var(--fg-secondary)", display: "block", marginBottom: "6px", fontWeight: "600" }}>
                    Description / Impact
                  </span>
                  <p style={{ margin: 0, fontSize: "12px", lineHeight: "1.5", color: "var(--fg-primary)" }}>
                    {drawerFinding.Description || drawerFinding.description}
                  </p>
                </div>
              )}

              {(drawerFinding.Solution || drawerFinding.solution || drawerFinding.Remediation) && (
                <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border-default)" }}>
                  <span style={{ fontSize: "12px", color: "var(--fg-secondary)", display: "block", marginBottom: "6px", fontWeight: "600" }}>
                    Remediation Guidance
                  </span>
                  <p style={{ margin: 0, fontSize: "12px", lineHeight: "1.5", color: "var(--fg-primary)" }}>
                    {drawerFinding.Solution || drawerFinding.solution || drawerFinding.Remediation}
                  </p>
                </div>
              )}
            </div>

            <div style={{ padding: "16px 20px", borderTop: "1px solid var(--border-default)" }}>
              <Link
                to={
                  drawerFinding._taskDataId || latestTaskId
                    ? `/exposedblackbox/finding/${drawerFinding._taskDataId || latestTaskId}/${drawerFinding.ID || drawerFinding._index}`
                    : "#"
                }
                className="blackbox-btn btn-primary"
                style={{ width: "100%", height: "36px", textDecoration: "none" }}
              >
                Open Full Detail &amp; Remediation Page →
              </Link>
            </div>
          </aside>
        </>
      )}

      {/* Modal: Authorize New Scope (from Verified Asset Inventory) */}
      <Modal
        isOpen={modalCreateScope}
        toggle={() => setModalCreateScope(!modalCreateScope)}
        centered
        size="lg"
        wrapClassName={`blackbox-modal-portal ${theme === "dark" ? "theme-dark" : "theme-light"}`}
      >
        <div className="modal-header">
          <h5 className="modal-title" style={{ color: "var(--fg-primary)", fontSize: "16px", fontWeight: "600" }}>
            Authorize New Black-Box Testing Scope
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setModalCreateScope(false)}
            aria-label="Close"
            style={{ filter: theme === "dark" ? "invert(1) grayscale(100%) brightness(200%)" : "none" }}
          />
        </div>
        <div className="modal-body" style={{ background: "var(--bg-surface)", color: "var(--fg-primary)" }}>
          <p className="text-muted small mb-3">
            <strong>Mandatory Verification Check:</strong> You can only authorize targets that belong to your organization and have been verified in your Asset Inventory.
          </p>

          {verifiedAssets.length === 0 ? (
            <Alert color="warning" style={{ background: "var(--bg-surface-muted)", borderColor: "var(--orange-500)", color: "var(--orange-500)" }}>
              No verified Domain or IP assets were found in your Asset Inventory. Please verify domain/IP ownership in the Asset Inventory module first.
            </Alert>
          ) : (
            <AvForm onValidSubmit={handleCreateScopeSubmit}>
              <FormGroup className="mb-3">
                <Label className="fw-semibold small" style={{ color: "var(--fg-secondary)" }}>
                  Verified Target (from Asset Inventory)
                </Label>
                <select
                  className="form-select"
                  value={newScopeTarget}
                  onChange={(e) => {
                    setNewScopeTarget(e.target.value);
                    const matched = verifiedAssets.find((a) => a.target === e.target.value);
                    if (matched) setNewScopeTargetType(matched.type);
                  }}
                  style={{ background: "var(--bg-surface-muted)", color: "var(--fg-primary)", borderColor: "var(--border-default)" }}
                  required
                >
                  {verifiedAssets.map((asset) => (
                    <option key={asset.id + asset.target} value={asset.target}>
                      {asset.target} [{asset.type.toUpperCase()}] — Verified via {asset.source}
                    </option>
                  ))}
                </select>
              </FormGroup>

              <FormGroup className="mb-3">
                <Label className="fw-semibold small d-block" style={{ color: "var(--fg-secondary)" }}>
                  Allowed Scan Types
                </Label>
                {ALL_SCAN_TYPES.map((type) => {
                  const isChecked = newScopeScanTypes.includes(type.id);
                  return (
                    <div key={type.id} className="form-check form-check-inline mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`type-${type.id}`}
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewScopeScanTypes([...newScopeScanTypes, type.id]);
                            if (type.intrusive) setNewScopeIntrusive(true);
                          } else {
                            const updated = newScopeScanTypes.filter((t) => t !== type.id);
                            setNewScopeScanTypes(updated);
                            const hasOtherIntrusive = updated.some((id) => {
                              const match = ALL_SCAN_TYPES.find((m) => m.id === id);
                              return match?.intrusive;
                            });
                            if (!hasOtherIntrusive) setNewScopeIntrusive(false);
                          }
                        }}
                      />
                      <label className="form-check-label small" htmlFor={`type-${type.id}`} style={{ color: "var(--fg-primary)" }}>
                        {type.label}
                      </label>
                    </div>
                  );
                })}
              </FormGroup>

              <FormGroup className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="newScopeIntrusiveCheck"
                  checked={newScopeIntrusive}
                  onChange={(e) => setNewScopeIntrusive(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="newScopeIntrusiveCheck" style={{ color: "var(--fg-primary)" }}>
                  Allow Intrusive Assessment Mode (optional)
                </label>
              </FormGroup>

              <FormGroup className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="newScopeVaptCheck"
                  checked={newScopeVapt}
                  onChange={(e) => setNewScopeVapt(e.target.checked)}
                />
                <label className="form-check-label small" htmlFor="newScopeVaptCheck" style={{ color: "var(--fg-primary)" }}>
                  Authorize VAPT (Penetration Testing) Mode (optional)
                </label>
                <div style={{ fontSize: "11px", color: "var(--fg-secondary)", marginTop: "2px" }}>
                  Permits active exploitation attempts beyond vulnerability scanning on this scope target.
                </div>
              </FormGroup>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <FormGroup className="mb-3">
                  <Label className="fw-semibold small" style={{ color: "var(--fg-secondary)" }}>
                    Testing Window Start (HH:MM)
                  </Label>
                  <Input
                    type="time"
                    value={newScopeWindowStart}
                    onChange={(e) => setNewScopeWindowStart(e.target.value)}
                    style={{ background: "var(--bg-surface-muted)", color: "var(--fg-primary)", borderColor: "var(--border-default)" }}
                  />
                  <small className="text-muted">Leave blank for 24/7 scanning.</small>
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label className="fw-semibold small" style={{ color: "var(--fg-secondary)" }}>
                    Testing Window End (HH:MM)
                  </Label>
                  <Input
                    type="time"
                    value={newScopeWindowEnd}
                    onChange={(e) => setNewScopeWindowEnd(e.target.value)}
                    style={{ background: "var(--bg-surface-muted)", color: "var(--fg-primary)", borderColor: "var(--border-default)" }}
                  />
                  <small className="text-muted">Scans outside this window will be blocked.</small>
                </FormGroup>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <FormGroup className="mb-3">
                  <Label className="fw-semibold small" style={{ color: "var(--fg-secondary)" }}>
                    Scope Expiration Date
                  </Label>
                  <Input
                    type="date"
                    value={newScopeExpiry}
                    onChange={(e) => setNewScopeExpiry(e.target.value)}
                    style={{ background: "var(--bg-surface-muted)", color: "var(--fg-primary)", borderColor: "var(--border-default)" }}
                  />
                  <small className="text-muted">Optional automatic expiration date.</small>
                </FormGroup>

                <FormGroup className="mb-3">
                  <Label className="fw-semibold small" style={{ color: "var(--fg-secondary)" }}>
                    Concurrent Scan Limit
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    max="5"
                    value={newScopeRateLimit}
                    onChange={(e) => setNewScopeRateLimit(e.target.value)}
                    style={{ background: "var(--bg-surface-muted)", color: "var(--fg-primary)", borderColor: "var(--border-default)" }}
                    required
                  />
                  <small className="text-muted">Max simultaneous scans for this scope.</small>
                </FormGroup>
              </div>

              <div className="d-flex justify-content-end gap-2 mt-4 pt-2" style={{ borderTop: "1px solid var(--border-default, #e2e8f0)" }}>
                <Button
                  color="secondary"
                  outline
                  type="button"
                  onClick={() => setModalCreateScope(false)}
                  style={{
                    borderColor: theme === "dark" ? "#26324a" : "#cbd5e1",
                    color: theme === "dark" ? "#e6ebf2" : "#334155",
                    background: "transparent",
                    fontWeight: "500",
                    fontSize: "13px",
                    padding: "8px 18px",
                    borderRadius: "6px",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  disabled={isCreatingScope}
                  style={{
                    backgroundColor: "#2f6fed",
                    borderColor: "#2f6fed",
                    color: "#ffffff",
                    fontWeight: "600",
                    fontSize: "13px",
                    padding: "8px 22px",
                    borderRadius: "6px",
                    boxShadow: "0 2px 6px rgba(47, 111, 237, 0.3)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    cursor: isCreatingScope ? "not-allowed" : "pointer",
                    minWidth: "190px",
                  }}
                >
                  {isCreatingScope ? (
                    <>
                      <Spinner size="sm" color="light" />
                      <span>Authorizing...</span>
                    </>
                  ) : (
                    "Authorize Target Scope"
                  )}
                </Button>
              </div>
            </AvForm>
          )}
        </div>
      </Modal>

      {/* Modal: Manage Scopes List */}
      <Modal
        isOpen={modalManageScopes}
        toggle={() => setModalManageScopes(!modalManageScopes)}
        centered
        size="lg"
        wrapClassName={`blackbox-modal-portal ${theme === "dark" ? "theme-dark" : "theme-light"}`}
      >
        <div className="modal-header">
          <h5 className="modal-title" style={{ color: "var(--fg-primary)", fontSize: "16px", fontWeight: "600" }}>
            Authorized Scopes Management
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setModalManageScopes(false)}
            aria-label="Close"
            style={{ filter: theme === "dark" ? "invert(1) grayscale(100%) brightness(200%)" : "none" }}
          />
        </div>
        <div className="modal-body" style={{ background: "var(--bg-surface)", color: "var(--fg-primary)" }}>
          {scopes.length === 0 ? (
            <p className="text-muted text-center py-3">No authorized scopes configured yet.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle" style={{ borderColor: "var(--border-default)", color: "var(--fg-primary)" }}>
                <thead style={{ background: "var(--bg-surface-muted)" }}>
                  <tr>
                    <th style={{ color: "var(--fg-primary)", borderColor: "var(--border-default)" }}>Target</th>
                    <th style={{ color: "var(--fg-primary)", borderColor: "var(--border-default)" }}>Type</th>
                    <th style={{ color: "var(--fg-primary)", borderColor: "var(--border-default)" }}>Scan Types</th>
                    <th style={{ color: "var(--fg-primary)", borderColor: "var(--border-default)" }}>Window</th>
                    <th style={{ color: "var(--fg-primary)", borderColor: "var(--border-default)" }}>Status</th>
                    <th style={{ color: "var(--fg-primary)", borderColor: "var(--border-default)" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {scopes.map((s) => (
                    <tr key={s._id} style={{ borderColor: "var(--border-default)" }}>
                      <td style={{ borderColor: "var(--border-default)" }}>
                        <strong>{s.target}</strong>
                        <div className="small text-muted">Auth by: {s.authorizedBy}</div>
                      </td>
                      <td style={{ borderColor: "var(--border-default)" }}>
                        <span style={{ padding: "2px 8px", borderRadius: "var(--radius-sm)", background: "var(--slate-800)", border: "1px solid var(--border-default)" }}>
                          {s.targetType}
                        </span>
                      </td>
                      <td style={{ borderColor: "var(--border-default)" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          <small>{s.scanTypesAllowed?.join(", ") || "unauthenticated"}</small>
                          <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "10px", padding: "1px 5px", borderRadius: "3px", background: "rgba(47, 111, 237, 0.15)", color: "var(--brand-primary)", fontWeight: 600 }}>
                              VA
                            </span>
                            {s.vaptAllowed && (
                              <span style={{ fontSize: "10px", padding: "1px 5px", borderRadius: "3px", background: "rgba(139, 92, 246, 0.15)", color: "var(--purple-400)", fontWeight: 600 }}>
                                VAPT
                              </span>
                            )}
                            {s.intrusiveAllowed && (
                              <span style={{ fontSize: "10px", padding: "1px 5px", borderRadius: "3px", background: "rgba(220, 59, 44, 0.15)", color: "var(--red-500)", fontWeight: 600 }}>
                                Intrusive
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ borderColor: "var(--border-default)" }}>
                        <small>
                          {s.testingWindowStart && s.testingWindowEnd
                            ? `${s.testingWindowStart} - ${s.testingWindowEnd}`
                            : "24/7"}
                        </small>
                      </td>
                      <td style={{ borderColor: "var(--border-default)" }}>
                        <span
                          className={`blackbox-status-pill ${
                            s.status === "active" ? "status-completed" : "status-failed"
                          }`}
                        >
                          {s.status}
                        </span>
                      </td>
                      <td style={{ borderColor: "var(--border-default)" }}>
                        {s.status === "active" && canManageScope && (
                          <Button
                            color="danger"
                            size="sm"
                            outline
                            onClick={() => handleRevokeScope(s._id)}
                            style={{
                              borderColor: "#dc3b2c",
                              color: "#dc3b2c",
                              fontWeight: "600",
                              fontSize: "12px",
                              padding: "4px 12px",
                            }}
                          >
                            Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      {/* Modal: Scan History for the current target */}
      <Modal
        isOpen={modalScanHistory}
        toggle={() => setModalScanHistory(!modalScanHistory)}
        centered
        size="xl"
        style={{
          position: "relative",
          left: `${historySidebarOffset / 2}px`,
          maxWidth: `min(1140px, calc(100vw - ${historySidebarOffset}px - 48px))`,
        }}
        wrapClassName={`blackbox-modal-portal ${theme === "dark" ? "theme-dark" : "theme-light"}`}
      >
        <div className="blackbox-tokens" data-theme={theme}>
          <div
            className="modal-header"
            style={{ padding: "16px 20px", display: "flex", alignItems: "flex-start", gap: "12px" }}
          >
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h5 className="modal-title m-0" style={{ fontSize: "16px", fontWeight: "600" }}>
                  Scan History
                </h5>
                {!isLoadingHistory && (
                  <span
                    style={{
                      fontSize: "11px",
                      fontFamily: "var(--font-mono)",
                      color: "var(--fg-secondary)",
                      background: "var(--slate-800)",
                      padding: "1px 7px",
                      borderRadius: "999px",
                    }}
                  >
                    {scanHistory.length} scans
                  </span>
                )}
              </div>
              <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                Target:{" "}
                <b style={{ fontFamily: "var(--font-mono)", fontWeight: "600", color: "var(--fg-primary)" }}>
                  {latestScanInfo?.target || selectedScope?.target || "—"}
                </b>{" "}
                · Completed, killed and failed scans
              </span>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setModalScanHistory(false)}
              aria-label="Close"
              style={{ filter: theme === "dark" ? "invert(1) grayscale(100%) brightness(200%)" : "none" }}
            />
          </div>
          <div className="modal-body" style={{ padding: 0 }}>
            {isLoadingHistory ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "48px 16px", color: "var(--fg-secondary)", fontSize: "13px" }}>
                <Spinner size="sm" color="primary" /> Loading scan history...
              </div>
            ) : scanHistory.length === 0 ? (
              <div style={{ padding: "48px 16px", textAlign: "center", display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--fg-primary)" }}>No past scans yet</span>
                <span style={{ fontSize: "12px", color: "var(--fg-secondary)" }}>
                  Completed, killed or failed scans for this target will appear here.
                </span>
              </div>
            ) : (
              <div className="blackbox-table-wrap" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                <table className="blackbox-table">
                  <thead>
                    <tr>
                      <th>Scan</th>
                      <th>Mode</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th style={{ textAlign: "right" }}>Findings</th>
                      <th style={{ textAlign: "right" }}>Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanHistory.map((s) => {
                      const scanDate = new Date(s.completedAt || s.killedAt || s.startedAt);
                      const hasDate = !isNaN(scanDate.getTime());
                      const isCurrent = (latestScanInfo?.scanId && s.scanId === latestScanInfo.scanId) || s.taskDataId === String(latestTaskId);
                      return (
                        <tr key={s.taskDataId}>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                              <span style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: "600" }}>
                                {s.scanName || "Untitled Scan"}
                                {isCurrent && (
                                  <span style={{ fontSize: "10px", fontWeight: "600", padding: "1px 6px", borderRadius: "999px", background: "var(--slate-800)", color: "var(--fg-secondary)" }}>
                                    Current
                                  </span>
                                )}
                              </span>
                              <span
                                title={s.scanId || s.taskDataId}
                                style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--fg-tertiary)" }}
                              >
                                {s.scanId || s.taskDataId}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                              {renderKindBadge(s.kind)}
                              <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>{s.scanType || "unauthenticated"}</span>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`blackbox-status-pill ${
                                s.status === "completed" ? "status-completed" : s.status === "killed" ? "status-killed" : "status-failed"
                              }`}
                              style={{ textTransform: "capitalize" }}
                            >
                              <span className="dot"></span>
                              {s.status}
                            </span>
                          </td>
                          <td>
                            {hasDate ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                <span style={{ fontSize: "12px" }}>{scanDate.toLocaleDateString()}</span>
                                <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>{scanDate.toLocaleTimeString()}</span>
                              </div>
                            ) : (
                              <span style={{ color: "var(--fg-tertiary)" }}>—</span>
                            )}
                          </td>
                          <td style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: "600" }}>
                            {s.status === "completed" ? s.findingsCount : <span style={{ color: "var(--fg-tertiary)", fontWeight: "400" }}>—</span>}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            {s.status === "completed" && s.scanId ? (
                              <button
                                className="blackbox-btn"
                                style={{ height: "28px", minWidth: "72px", fontSize: "12px" }}
                                onClick={() => handleDownloadHistoryReport(s)}
                                disabled={downloadingTaskId === s.taskDataId}
                              >
                                {downloadingTaskId === s.taskDataId ? (
                                  <Spinner size="sm" style={{ width: "12px", height: "12px", borderWidth: "2px" }} />
                                ) : (
                                  <>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                    </svg>
                                    PDF
                                  </>
                                )}
                              </button>
                            ) : (
                              <span style={{ fontSize: "12px", color: "var(--fg-tertiary)" }}>Not available</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ExposedBlackBox;
